import { ATTRIBUTE_OBJECTS, consoleRoutes, WORKFLOW_LIMITS, type AttributeType } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Field, Input, Select } from "@sagot/ui";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import { attributesOf } from "../../../workspace/shared";
import { sanitizeKey, targetKey } from "./collect-model";
import { useRefs } from "./refs";
import { isTypedKeyTarget, replySaveOf, replySaveToOf, retarget, type ReplyTarget } from "./reply-model";

const F = "engage.workflows.fields";

interface ReplySaveFieldProps {
  label: string;
  /** `visitor.email` / `var.answer` / `visitor.attr.grade` ... */
  value: string;
  targets: readonly ReplyTarget[];
  /** The custom attributes the reply can go to, by their type (a reply is text; a number attribute fits a number). */
  attributeTypes: readonly AttributeType[];
  /** The key a new `var.` / `lead.` target starts with. */
  fallbackKey: string;
  disabled: boolean;
  /** Told when the target kind changes, so the form can fix what depends on it (the format of an e-mail reply). */
  onChange: (saveTo: string, previous: string) => void;
}

/** Where a collected value goes: a kind of place, then the field (a key to type for a variable or lead field, a defined attribute to pick). */
export function ReplySaveField({ label, value, targets, attributeTypes, fallbackKey, disabled, onChange }: ReplySaveFieldProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const { attributes: definitions } = useRefs();
  const save = replySaveOf(value);
  const choices = ATTRIBUTE_OBJECTS.map((object) => ({ object, items: attributesOf(definitions, object).filter((item) => attributeTypes.includes(item.type)) })).filter((group) => group.items.length > 0);
  const known = choices.some((group) => group.items.some((item) => group.object === save.object && item.key === save.key));
  return (
    <div className="space-y-3">
      <Field label={label}>
        <Select value={save.target} disabled={disabled} onChange={(e) => { onChange(retarget(save, e.target.value as ReplyTarget, fallbackKey), value); }}>
          {targets.map((target) => <option key={target} value={target}>{t(`${F}.saveTargets.${targetKey(target)}`)}</option>)}
        </Select>
      </Field>
      {isTypedKeyTarget(save.target) ? (
        <Field label={t(`${F}.saveKey`)} hint={t(`${F}.saveKeyHint`)}>
          <Input className="font-mono text-xs" maxLength={WORKFLOW_LIMITS.customKey} disabled={disabled} value={save.key} onChange={(e) => { onChange(replySaveToOf({ ...save, key: sanitizeKey(e.target.value) }), value); }} />
        </Field>
      ) : null}
      {save.target === "attribute" ? (
        <>
          <Field label={t(`${F}.attributeKey`)}>
            <Select
              value={save.key ? `${save.object}.attr.${save.key}` : ""}
              disabled={disabled || choices.length === 0}
              onChange={(e) => { onChange(e.target.value || replySaveToOf({ ...save, key: "" }), value); }}
            >
              <option value="">{t(`${F}.choose`)}</option>
              {save.key && !known ? <option value={value}>{value}</option> : null}
              {choices.map((group) => (
                <optgroup key={group.object} label={t(`settings.attributes.objects.${group.object}`)}>
                  {group.items.map((item) => <option key={item.key} value={`${group.object}.attr.${item.key}`}>{item.label}</option>)}
                </optgroup>
              ))}
            </Select>
          </Field>
          {choices.length === 0 ? (
            <p className="text-xs text-fg-muted" data-testid="attributes-empty">
              {t(`${F}.noAttributes`)}{" "}
              <AppLink className="font-semibold text-primary-ink underline" href={consoleRoutes.settings(summary.slug, "attributes")}>{t(`${F}.openAttributes`)}</AppLink>
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
